import React from 'react'
import _ from 'lodash/fp'
import withStyles from '@material-ui/core/styles/withStyles'
import classNames from 'classnames'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'

import colors from '@fe/ui/colors'

import { ArrowDropDownCircleOutlined as ShowMoreIcon } from '@material-ui/icons'
const scrollDown = () => {
  window.scrollTo({ top: window.innerHeight - 64, behavior: 'smooth' })
}

const borderWidth = 1
const styles = {
  container: {
    color: 'white',
    margin: 12,
    width: 'unset',
    flexDirection: 'column'
  },
  cell: {
    position: 'relative',
    border: `solid white ${borderWidth}px`,
    marginLeft: -borderWidth,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '8.5vw',
    maxWidth: '2.5em',
    fontWeight: 'bold',
    '&:after': {
      content: '""',
      display: 'block',
      paddingBottom: '100%'
    }
  },
  header: {
    fontWeight: 'lighter'
  },
  total: {
    background: 'rgba(255,255,255, 0.8)',
    color: colors.primaryDarkGreen
  },
  row: {
    marginTop: -borderWidth
  },
  label: {
    border: `solid white ${borderWidth}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '5em'
  },
  smallIcon: {
    fontSize: '3em',
    margin: 0,
    cursor: 'pointer'
  }
}
const wrapperStyle = {
  border: 'solid white 1px',
  width: '88%',
  height: '88%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto'
}

const wrapHole = hole => {
  const addBorder = (component, points) => {
    if (component === 0) { // comparing component to 0 is only valid in the first iteration, where component = hole.nShots
      return '-'
    }
    if (points === 2) { // if points is 2 then there is no further bordering to be done
      return component
    } else if (points > 2) { // if points is > 2 then the borders are to be round, because round indicates well played
      return addBorder(<div style={{ ...wrapperStyle, borderRadius: '50%' }}>{component}</div>, points - 1)
    }
    // points must be 1 or less, so add square borders to indicate how shit the player is
    return addBorder(<div style={wrapperStyle}>{component}</div>, points + 1)
  }
  // maximum number of borders must be 2, having max 4 points ensures max 2 iterations of addBorder
  return (
    <>
      {addBorder(hole.nShots, Math.min(4, _.get('comparePoints', hole)))}
    </>
  )
}

const Rows = ({ classes, holes, big }) => {
  const moreThan9Holes = _.size(holes) > 9

  const createRow = ({ label, map, total }) => (
    [
      label,
      map(_.take(9, holes)),
      total(_.take(9, holes)),
      ...(moreThan9Holes ? [...map(_.drop(9, holes)), total(_.drop(9, holes))] : [])
    ]
  )

  const uniqueId = () => _.uniqueId('scorecard_')

  const holeTotal = label => (<Grid key={label} className={classNames(classes.cell, classes.header, classes.total)} item>{label}</Grid>)
  const shotsTotal = holes => (<Grid key={uniqueId()} className={classNames(classes.cell, classes.total)} item>{_.sumBy('nShots', holes)}</Grid>)
  const pointsTotal = holes => (<Grid key={uniqueId()} className={classNames(classes.cell, classes.total)} item>{_.sumBy('points', holes)}</Grid>)

  const shotsLabel = (
    <React.Fragment key={uniqueId()}>
      <Hidden mdDown={!big} smDown={big}>
        <Grid className={classNames(classes.label, classes.header)} item>Shots</Grid>
      </Hidden>
      <Hidden lgUp={!big} mdUp={big}>
        <Grid className={classNames(classes.cell, classes.header)} item>S</Grid>
      </Hidden>
    </React.Fragment>
  )
  const pointsLabel = (
    <React.Fragment key={uniqueId()}>
      <Hidden mdDown={!big} smDown={big}>
        <Grid className={classNames(classes.label, classes.header)} item>Points</Grid>
      </Hidden>
      <Hidden lgUp={!big} mdUp={big}>
        <Grid className={classNames(classes.cell, classes.header)} item>P</Grid>
      </Hidden>
    </React.Fragment>
  )
  const holeMap = _.map(h => <Grid className={classNames(classes.cell, classes.header)} item key={h.holeId}>{h.holeOrder}</Grid>)
  const shotsMap = _.map(h => <Grid className={classNames(classes.cell)} item key={h.holeId}>{wrapHole(h)}</Grid>)
  const pointsMap = _.map(h => <Grid className={classNames(classes.cell)} item key={h.holeId}>{h.nShots === 0 ? '-' : h.points}</Grid>)

  const holeRow = [
    holeMap(_.take(9, holes)),
    holeTotal('out'),
    ...(moreThan9Holes ? [...holeMap(_.drop(9, holes)), holeTotal('in')] : [])
  ]

  const shotsRow = createRow({
    label: shotsLabel,
    map: shotsMap,
    total: shotsTotal
  })

  const pointsRow = createRow({
    label: pointsLabel,
    map: pointsMap,
    total: pointsTotal
  })

  return (
    <>
      <Grid item className={classes.row}>
        <Grid container justify='flex-end'>
          {holeRow}
        </Grid>
      </Grid>
      <Grid item className={classes.row}>
        <Grid container justify='flex-end'>
          {shotsRow}
        </Grid>
      </Grid>
      <Grid item className={classes.row}>
        <Grid container justify='flex-end'>
          {pointsRow}
        </Grid>
      </Grid>
    </>
  )
}

const ScoreCard = ({ classes, holes, label, standalone = false, big = false }) => {
  const totalShots = _.sumBy('nShots', holes)
  const totalPoints = _.sumBy('points', holes)
  return (
    <Grid container className={classes.container} justify='flex-end'>
      <Grid item className={classes.row} style={{ marginBottom: 12, fontWeight: 'lighter', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'flex-end' }}>
        {!standalone && (
          <Hidden lgUp={!big} mdUp={big}>
            <div xs={12} style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', width: '100%' }}>
              <div
                onClick={scrollDown}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ShowMoreIcon style={styles.smallIcon} />
              </div>
            </div>
          </Hidden>
        )}
        <Typography style={{ color: colors.white, fontSize: 'calc(2em + 3vw)' }}>{totalShots}</Typography>
        <Typography style={{ color: colors.white, fontSize: 'calc(1em + 3vw)' }}>s</Typography>
        <Typography style={{ color: colors.white, fontSize: 'calc(2em + 3vw)' }}>/</Typography>
        <Typography style={{ color: colors.white, fontSize: 'calc(2em + 3vw)' }}>{totalPoints}</Typography>
        <Typography style={{ color: colors.white, fontSize: 'calc(1em + 3vw)' }}>p</Typography>
      </Grid>
      <Hidden mdDown={!big} smDown={big}>
        <div>
          <Rows classes={classes} holes={holes} label='out' big={big} />
        </div>
      </Hidden>
      <Hidden lgUp={!big} mdUp={big}>
        {_.size(holes) > 9
          ? (
            <div>
              <Rows classes={classes} holes={_.take(9, holes)} label='out' style={{ marginBottom: 24 }} />
              <Rows classes={classes} holes={_.drop(9, holes)} label='in' />
            </div>
            )
          : <Rows classes={classes} holes={holes} label='out' />}
      </Hidden>
    </Grid>
  )
}

export default _.compose(
  withStyles(styles)
)(ScoreCard)
